/**
 * Frontend capability matrix — mirrors `packages/nbs-auth` capabilities.
 * Kept browser-safe (no `@nbs/auth` import: that package pulls `node:crypto`).
 */

export type ApiRole = "PUBLIC" | "CONSULTANT" | "SCIENTIST" | "REVIEWER" | "MANAGER" | "ADMIN";

export type Capability =
  | "nav.home"
  | "nav.dashboard"
  | "nav.hub"
  | "nav.indicators"
  | "nav.measures"
  | "nav.n1"
  | "nav.review"
  | "nav.settings"
  | "data.publishedMeasures"
  | "data.nonPublishedMeasures"
  | "data.proposals"
  | "data.fullProvenance"
  | "data.rawExtraction"
  | "data.internalNotes"
  | "data.auditHistory"
  | "action.reviewExtraction"
  | "action.scientificValidate"
  | "action.publishMeasure"
  | "action.manageUsers"
  | "action.manageRoles"
  | "export.published"
  | "export.internal"
  | "ui.roleBanner"
  | "ui.reviewerExtras"
  | "ui.scientistExtras"
  | "ui.managerExtras"
  | "ui.adminExtras";

export type DataDetailLevel = "public" | "viewer" | "reviewer" | "scientist" | "admin";

const ALL: readonly Capability[] = [
  "nav.home",
  "nav.dashboard",
  "nav.hub",
  "nav.indicators",
  "nav.measures",
  "nav.n1",
  "nav.review",
  "nav.settings",
  "data.publishedMeasures",
  "data.nonPublishedMeasures",
  "data.proposals",
  "data.fullProvenance",
  "data.rawExtraction",
  "data.internalNotes",
  "data.auditHistory",
  "action.reviewExtraction",
  "action.scientificValidate",
  "action.publishMeasure",
  "action.manageUsers",
  "action.manageRoles",
  "export.published",
  "export.internal",
  "ui.roleBanner",
  "ui.reviewerExtras",
  "ui.scientistExtras",
  "ui.managerExtras",
  "ui.adminExtras",
];

/** Public consultation: home, dashboard, catalogs, strategic N1 — no review/settings/admin. */
const PUBLIC_CAPS: readonly Capability[] = [
  "nav.home",
  "nav.dashboard",
  "nav.hub",
  "nav.indicators",
  "nav.measures",
  "nav.n1",
  "data.publishedMeasures",
  "ui.roleBanner",
];

const VIEWER_CAPS: readonly Capability[] = [
  "nav.home",
  "nav.dashboard",
  "nav.hub",
  "nav.indicators",
  "nav.measures",
  "nav.n1",
  "nav.settings",
  "data.publishedMeasures",
  "data.fullProvenance",
  "export.published",
  "ui.roleBanner",
];

const SCIENTIST_CAPS: readonly Capability[] = [
  ...VIEWER_CAPS,
  "nav.review",
  "data.nonPublishedMeasures",
  "data.proposals",
  "data.rawExtraction",
  "data.auditHistory",
  "action.scientificValidate",
  "action.reviewExtraction",
  "export.internal",
  "ui.scientistExtras",
];

const REVIEWER_CAPS: readonly Capability[] = [
  ...VIEWER_CAPS,
  "nav.review",
  "data.nonPublishedMeasures",
  "data.proposals",
  "data.rawExtraction",
  "data.internalNotes",
  "data.auditHistory",
  "action.reviewExtraction",
  "export.internal",
  "ui.reviewerExtras",
];

const MANAGER_CAPS: readonly Capability[] = [
  ...VIEWER_CAPS,
  "data.nonPublishedMeasures",
  "data.rawExtraction",
  "data.auditHistory",
  "action.publishMeasure",
  "export.internal",
  "ui.managerExtras",
];

export const ROLE_CAPABILITIES: Readonly<Record<ApiRole, readonly Capability[]>> = {
  PUBLIC: PUBLIC_CAPS,
  CONSULTANT: VIEWER_CAPS,
  SCIENTIST: SCIENTIST_CAPS,
  REVIEWER: REVIEWER_CAPS,
  MANAGER: MANAGER_CAPS,
  ADMIN: ALL,
};

export function isApiRole(value: string): value is ApiRole {
  return value in ROLE_CAPABILITIES;
}

export function capabilitiesForRole(role: ApiRole): readonly Capability[] {
  return ROLE_CAPABILITIES[role];
}

export function capabilitiesForRoles(roles: readonly string[]): readonly Capability[] {
  const set = new Set<Capability>();
  for (const role of roles) {
    if (isApiRole(role)) {
      for (const cap of ROLE_CAPABILITIES[role]) set.add(cap);
    }
  }
  if (set.size === 0) {
    for (const cap of PUBLIC_CAPS) set.add(cap);
  }
  return [...set];
}

export function roleHasCapability(role: ApiRole, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

export function measureStatusesForRole(role: ApiRole): readonly string[] | null {
  switch (role) {
    case "PUBLIC":
    case "CONSULTANT":
      return ["publiee"];
    case "SCIENTIST":
      return ["publiee", "validee", "controlee"];
    case "REVIEWER":
    case "MANAGER":
    case "ADMIN":
      return null;
    default:
      return ["publiee"];
  }
}

export function dataDetailLevelForRole(role: ApiRole): DataDetailLevel {
  switch (role) {
    case "PUBLIC":
      return "public";
    case "CONSULTANT":
      return "viewer";
    case "SCIENTIST":
      return "scientist";
    case "REVIEWER":
      return "reviewer";
    case "MANAGER":
    case "ADMIN":
      return "admin";
    default:
      return "public";
  }
}

export const PRODUCT_ROLE_LABEL: Record<string, string> = {
  PUBLIC: "public",
  CONSULTANT: "viewer",
  SCIENTIST: "scientist",
  REVIEWER: "reviewer",
  MANAGER: "manager",
  ADMIN: "admin",
};

export function primaryRole(roles: readonly string[]): string {
  const priority = ["ADMIN", "MANAGER", "REVIEWER", "SCIENTIST", "CONSULTANT", "PUBLIC"];
  for (const role of priority) {
    if (roles.includes(role)) return role;
  }
  return roles[0] ?? "PUBLIC";
}
